/**
 * Action Registry — wires global action handlers to the delegation system.
 *
 * All actions used via data-action="" must be registered here.
 * Import and call registerAllActions() from app.ts after window globals are set.
 */

import { navigate } from '../../router.js'
import { copyToClip } from './copyButton.js'
import { toggleGalaxy } from './sidebar.js'
import { registerAction } from './action-delegation.js'

// Window globals set by app.ts and view modules — typed here to avoid `any`
declare global {
  interface Window {
    showKbdHelp?: () => void
    openDrawer?: (arg: string) => void | Promise<void>
    closeDrawer?: () => void
    togSec?: (el: HTMLElement) => void
    switchDrawerTab?: (btn: HTMLElement) => void
    setKbTypeFilter?: (type: string) => void
    setKbSort?: (sort: string) => void
    setKbSearch?: (value: string) => void
    toggleKbTag?: (tag: string) => void
    clearKbFilters?: () => void
    goToKbWithTag?: (tag: string) => void
    setKbPage?: (page: number) => void
    setKbView?: (view: string) => void
    setKbUsageSort?: (spec: string) => void
    setReadsType?: (type: string) => void
    setAnalyticsDays?: (days: string) => void
    closeGraphPanel?: () => void
    selectGraphNode?: (id: string) => void
    openReadsCell?: (key: string) => void
    closeReadsCell?: () => void
    selectAgentLogRun?: (runId: string) => void
    selectAgentLogFile?: (filePath: string) => void
    refreshAgentLog?: (runId: string, filePath: string) => void
    toggleAgentLogTurn?: (turnIdx: string) => void
    toggleAgentLogThinking?: (idx: string) => void
    openCommandPalette?: () => void
    setWorkTypeFilter?: (type: string | null) => void
    setWorkStatusFilter?: (status: string | null) => void
    setWorkPriorityFilter?: (priority: string | null) => void
    setWorkAssignee?: (assignee: string | null) => void
    clearWorkFilters?: () => void
    setWorkPage?: (page: number) => void
    toggleWorkTag?: (tag: string) => void
    setWorkSort?: (spec: string) => void
    setWorkSearchInput?: (value: string) => void
  }
}

// showKbdHelp is defined in app.ts and exposed on window — access via window to avoid circular import

/** TogSec uses event delegation — finds .sec-head from click target. */
function handleTogSec(_arg: string, event: UIEvent): void {
  const el = event.target as Element
  el.closest('.sec-head')?.closest('.sec')?.classList.toggle('open')
}

/** Copy-to-clip — text is in data-arg, button element from event. */
function handleCopyToClip(arg: string, event: UIEvent): void {
  copyToClip(arg, event.currentTarget as HTMLElement)
}

/** Navigate — path is in data-arg. */
function handleNavigate(arg: string, _event: UIEvent): void {
  navigate(arg)
}

/** Toggle galaxy atmosphere. */
function handleToggleGalaxy(_arg: string, _event: UIEvent): void {
  toggleGalaxy()
}

/** Show keyboard shortcuts overlay. */
function handleShowKbdHelp(): void {
  window.showKbdHelp?.()
}

/** Open the command palette — real palette lives in src/components/commandPalette.ts,
 * exposed to window by app.ts. Previously this looked for `#command-palette`
 * which the real module never creates (it uses `#cmd-palette-overlay`), so
 * the header "Search ⌘K" button silently no-op'd. */
function handleOpenCommandPalette(): void {
  window.openCommandPalette?.()
}

/** Work: parse data-arg as the filter string OR 'null' to clear. */
function parseFilterArg(arg: string): string | null {
  return arg === 'null' || arg === '' ? null : arg
}

function handleSetWorkTypeFilter(arg: string): void {
  window.setWorkTypeFilter?.(parseFilterArg(arg))
}
function handleSetWorkStatusFilter(arg: string): void {
  window.setWorkStatusFilter?.(parseFilterArg(arg))
}
function handleSetWorkPriorityFilter(arg: string): void {
  window.setWorkPriorityFilter?.(parseFilterArg(arg))
}
function handleSetWorkAssignee(arg: string): void {
  // '@me' is a real value (server resolves it), so it must survive
  // parseFilterArg — only 'null'/'' clear the filter.
  window.setWorkAssignee?.(parseFilterArg(arg))
}
function handleClearWorkFilters(): void {
  window.clearWorkFilters?.()
}
function handleSetWorkPage(arg: string): void {
  window.setWorkPage?.(parseInt(arg, 10))
}

function handleToggleWorkTag(arg: string): void {
  window.toggleWorkTag?.(arg)
}

function handleSetWorkSort(arg: string): void {
  window.setWorkSort?.(arg)
}

/** Open the stage detail drawer — window global defined in run.ts. */
function handleOpenDrawer(arg: string): void {
  void window.openDrawer?.(arg)
}

/** Close the stage detail drawer — window global defined in run.ts. */
function handleCloseDrawer(): void {
  window.closeDrawer?.()
}

/** KB: set type filter. */
function handleSetKbTypeFilter(arg: string): void {
  window.setKbTypeFilter?.(arg)
}

/** KB: set sort order. */
function handleSetKbSort(arg: string): void {
  window.setKbSort?.(arg)
}

/** KB: toggle tag filter. */
function handleToggleKbTag(arg: string): void {
  window.toggleKbTag?.(arg)
}

/** KB: clear all filters. */
function handleClearKbFilters(): void {
  window.clearKbFilters?.()
}

/** KB: navigate to KB filtered by tag. */
function handleGoToKbWithTag(arg: string): void {
  window.goToKbWithTag?.(arg)
}

/** KB: set page number (data-arg is string, parse to int). */
function handleSetKbPage(arg: string): void {
  window.setKbPage?.(parseInt(arg, 10))
}

function handleSetKbView(arg: string): void {
  window.setKbView?.(arg)
}

function handleSetKbUsageSort(arg: string): void {
  window.setKbUsageSort?.(arg)
}

/** Read Activity: filter the heatmap by KB type. 'null' clears. */
function handleSetReadsType(arg: string): void {
  window.setReadsType?.(arg)
}

/** Analytics: change the time window. arg is a day count. */
function handleSetAnalyticsDays(arg: string): void {
  window.setAnalyticsDays?.(arg)
}

/** Graph: close the entry panel and clear the highlight. */
function handleCloseGraphPanel(): void {
  window.closeGraphPanel?.()
}

/** Graph: move the selection to a neighbour listed in the panel. */
function handleSelectGraphNode(arg: string): void {
  window.selectGraphNode?.(arg)
}

/** Read Activity: open the evidence drawer for one cell. arg is `actor|day`. */
function handleOpenReadsCell(arg: string): void {
  window.openReadsCell?.(arg)
}

function handleCloseReadsCell(): void {
  window.closeReadsCell?.()
}

/** Switch drawer tab — arg is the data-dtab value of the clicked tab button. */
function handleSwitchDrawerTab(arg: string): void {
  const drawer = document.querySelector('.drawer')
  if (!drawer) {
    return
  }
  const tabsContainer = drawer.querySelector('.dr-tabs')
  const bodyContainer = drawer.querySelector('.dr-body')
  if (!tabsContainer || !bodyContainer) {
    return
  }
  const btn = tabsContainer.querySelector(`[data-dtab="${arg}"]`)
  if (!btn) {
    return
  }
  tabsContainer.querySelectorAll('.dr-tab').forEach((t) => {
    t.classList.remove('active')
  })
  bodyContainer.querySelectorAll('.dr-tc').forEach((c) => {
    c.classList.remove('active')
  })
  btn.classList.add('active')
  bodyContainer.querySelector(`#${arg}`)?.classList.add('active')
}

/** Toggle perf strip detail table — arg is the id of the detail div. */
function handleTogglePerfDetail(arg: string): void {
  const detail = document.getElementById(arg)
  const btn = document.getElementById('perf-toggle-btn')
  if (!detail || !btn) {
    return
  }
  const isHidden = detail.style.display === 'none'
  detail.style.display = isHidden ? 'block' : 'none'
  btn.textContent = isHidden ? 'Hide detail \u25b2' : 'Show detail \u25bc'
}

/** Toggle gate/consolidate card expand — walks up to .gate, toggles .gate-expand child. */
function handleToggleGateExpand(_arg: string, event: UIEvent): void {
  const gate = (event.target as Element).closest('.gate')
  if (!gate) {
    return
  }
  gate.querySelector('.gate-expand')?.classList.toggle('open')
  const isOpen = gate.querySelector('.gate-expand')?.classList.contains('open') ?? false
  gate.setAttribute('aria-expanded', String(isOpen))
}

/** Toggle BDD scenario steps — .bdd-sh header toggles its next sibling .bdd-steps. */
function handleToggleBddScenario(_arg: string, event: UIEvent): void {
  const header = (event.target as Element).closest('.bdd-sh')
  if (!header) {
    return
  }
  header.nextElementSibling?.classList.toggle('open')
  const isOpen = header.nextElementSibling?.classList.contains('open') ?? false
  header.setAttribute('aria-expanded', String(isOpen))
}

/** Register all action handlers. Call once from app.ts. */
export function registerAllActions(): void {
  registerAction('navigate', handleNavigate)
  registerAction('copyToClip', handleCopyToClip)
  registerAction('toggleGalaxy', handleToggleGalaxy)
  registerAction('showKbdHelp', handleShowKbdHelp)
  registerAction('openCommandPalette', handleOpenCommandPalette)
  registerAction('togSec', handleTogSec)
  registerAction('openDrawer', handleOpenDrawer)
  registerAction('closeDrawer', handleCloseDrawer)
  registerAction('setKbTypeFilter', handleSetKbTypeFilter)
  registerAction('setKbSort', handleSetKbSort)
  registerAction('toggleKbTag', handleToggleKbTag)
  registerAction('clearKbFilters', handleClearKbFilters)
  registerAction('goToKbWithTag', handleGoToKbWithTag)
  registerAction('setKbPage', handleSetKbPage)
  registerAction('setKbView', handleSetKbView)
  registerAction('setKbUsageSort', handleSetKbUsageSort)
  registerAction('setReadsType', handleSetReadsType)
  registerAction('setAnalyticsDays', handleSetAnalyticsDays)
  registerAction('closeGraphPanel', handleCloseGraphPanel)
  registerAction('selectGraphNode', handleSelectGraphNode)
  registerAction('openReadsCell', handleOpenReadsCell)
  registerAction('closeReadsCell', handleCloseReadsCell)
  registerAction('setWorkTypeFilter', handleSetWorkTypeFilter)
  registerAction('setWorkStatusFilter', handleSetWorkStatusFilter)
  registerAction('setWorkPriorityFilter', handleSetWorkPriorityFilter)
  registerAction('setWorkAssignee', handleSetWorkAssignee)
  registerAction('clearWorkFilters', handleClearWorkFilters)
  registerAction('setWorkPage', handleSetWorkPage)
  registerAction('toggleWorkTag', handleToggleWorkTag)
  registerAction('setWorkSort', handleSetWorkSort)
  registerAction('switchDrawerTab', handleSwitchDrawerTab)
  registerAction('togglePerfDetail', handleTogglePerfDetail)
  registerAction('toggleGateExpand', handleToggleGateExpand)
  registerAction('toggleBddScenario', handleToggleBddScenario)
}
